/** @jsxImportSource @emotion/react */
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import CustomDialog from 'components/modals/common/CustomDialog';
import CustomGrid from 'components/grids/CustomGrid';
import { getMpMgrChgHistoryList } from 'apis/mp/MpMrChg';
import { SubTitleGroup, SubTitleLayout } from 'components/layouts/ContentLayout';
import { MpMgrChgList } from 'models/mp/MpMgr';
import { useMessageBar } from 'hooks/useMessageBar';

export interface SearchParam {
  mpItemId?: string;
  mgrTpCd?: string;
  prdnPlntCd?: string;
}

interface Props {
  open: boolean;
  close: (isDone?: boolean) => void;
  title: string;
  condition: any;
}

/**
 * MP담당자 변경이력 조회 팝업 (읽기전용)
 * @param props
 * @constructor
 */
export const MpMgrChgHistoryPopUp = ({ open, close, title, condition }: Props) => {
  const { t } = useTranslation();
  const { openMessageBar } = useMessageBar();
  const [rowData, setRowData] = useState<SearchParam[]>([]);

  const initSearchParam = {
    mpItemId: '',
    mgrTpCd: '',
    prdnPlntCd: '',
  };

  const [searchParam, setSearchParam] = useState<SearchParam>(initSearchParam);
  const [searchState, setSearchState] = useState<boolean>(false);

  useEffect(() => {
    setSearchParam({
      mpItemId: condition?.mpItemId || '',
      mgrTpCd: condition?.mgrTpCd || '',
      prdnPlntCd: condition?.prdnPlntCd || '',
    });
    setSearchState(true);
  }, [condition]);

  useEffect(() => {
    if (searchState) {
      getMpMgrList();
    }
  }, [searchState]);

  const layoutDefinition = [
    {
      header: String(t('com.label.NO', 'NO')),
      align: 'center',
      cellTemplate: (grid) => grid.row._idx + 1,
      width: 40,
    },
    {
      binding: 'crgeUserId',
      header: String(t('com.label.사용자ID', '사용자ID')),
      align: 'left',
      width: 120,
    },
    {
      binding: 'crgeUserNm',
      header: String(t('com.label.사용자명', '사용자명')),
      align: 'center',
      width: 120,
    },
    {
      binding: 'chgRsnCtn',
      header: String(t('com.label.변경사유', '변경사유')),
      align: 'left',
      width: '*',
    },
  ];

  const getMpMgrList = async () => {
    await getMpMgrChgHistoryList(searchParam).then((result: MpMgrChgList[]) => {
      if (result !== null) {
        setRowData(result);
      } else {
        openMessageBar({
          type: 'error',
          content: t(
            'com.msg.요청 정보 조회 중 오류가 발생했습니다.',
            '요청 정보 조회 중 오류가 발생했습니다.'
          ),
        });
        return false;
      }
    });
  };

  const handleClose = () => {
    close();
  };

  return (
    <>
      <CustomDialog
        title={t('mp.label.담당자 변경이력', '담당자 변경이력')}
        open={open}
        onClose={handleClose}
        size="md"
      >
        <SubTitleLayout>
          <SubTitleGroup>
            <h3>{t('mp.label.담당자 변경이력 List', '담당자 변경이력 List')}</h3>
            <span className="total">
              {t('com.label.총', '총')}
              <span>{rowData?.length ?? 0}</span>
              {t('com.label.건', '건')}
            </span>
          </SubTitleGroup>
        </SubTitleLayout>
        <CustomGrid
          layoutDefinition={layoutDefinition}
          rowData={rowData}
          height={200}
          isReadOnly={true}
          isFilter={false}
          isSelector={false}
          allowPinning={false}
        />
      </CustomDialog>
    </>
  );
};
